import { Typography } from '@/components/atoms';
import { getWebPageContentPadding } from '@/core/constants/webLayout';
import { useUIColors, useWebViewport } from '@/core/hooks';
import { BREAKPOINTS } from '@/core/utils/responsive';
import { OverlayScrollView } from '@/modules/properties-web/components/OverlayScrollView';
import { useScrollPagination } from '@/modules/properties-web/hooks/useScrollPagination';
import React, { useEffect, useState } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import {
  ADHA_LISTING_MAIN_FLEX,
  ADHA_LISTING_SIDEBAR_FLEX,
  type AdhaListingLayout,
  type AdhaListingTab,
} from './adhaListingLayout';

/** Warm units pane after project tab paints so the first switch is not a cold mount. */
const UNITS_PANE_WARM_MS = 350;

export interface AdhaListingLayoutShellProps {
  readonly layout: AdhaListingLayout;
  readonly compactTab: AdhaListingTab;
  readonly onSelectCompactTab: (tab: AdhaListingTab) => void;
  readonly projectTabLabel: string;
  readonly unitsTabLabel: string;
  /** Desktop: greeting | Explore Units */
  readonly titleRow?: React.ReactNode;
  /** Desktop: status cards (or greeting when no cards) | price filter */
  readonly statusFilterRow?: React.ReactNode;
  /**
   * Desktop: price filter + Associated Units pinned above the units ScrollView
   * (stays sticky while unit cards scroll).
   */
  readonly sidebarSticky?: React.ReactNode;
  /** Compact: greeting above Project | Units tabs. */
  readonly compactHeader?: React.ReactNode;
  /**
   * Compact Project tab: reservation/appointment cards pinned under tabs
   * (not inside the project ScrollView).
   */
  readonly compactStatusSticky?: React.ReactNode;
  /**
   * Compact Units tab: price filter pinned under tabs
   * (not inside the units ScrollView).
   */
  readonly compactPriceSticky?: React.ReactNode;
  /** Desktop/compact project scroll body (hero + about…). */
  readonly projectColumn: React.ReactNode;
  /** Desktop/compact units scroll body (unit cards). */
  readonly unitsColumn: React.ReactNode;
  readonly unitsScrollPagination: ReturnType<typeof useScrollPagination>;
}

function CompactTabs({
  compactTab,
  isPhone,
  projectTabLabel,
  unitsTabLabel,
  onSelect,
}: Readonly<{
  compactTab: AdhaListingTab;
  isPhone: boolean;
  projectTabLabel: string;
  unitsTabLabel: string;
  onSelect: (tab: AdhaListingTab) => void;
}>) {
  const colors = useUIColors();
  const tabVariant = isPhone ? 'subhead' : 'headline';
  const projectSelected = compactTab === 'project';
  const unitsSelected = compactTab === 'units';

  return (
    <View style={styles.compactTabs}>
      <Pressable
        accessibilityRole="button"
        accessibilityState={{ selected: projectSelected }}
        onPress={() => onSelect('project')}
        style={[
          styles.compactTab,
          isPhone && styles.compactTabPhone,
          {
            backgroundColor: projectSelected
              ? colors.buttonDefault
              : colors.backgroundLevel1,
          },
        ]}
      >
        <Typography
          variant={tabVariant}
          color={projectSelected ? 'white' : 'primary'}
          style={styles.compactTabText}
        >
          {projectTabLabel}
        </Typography>
      </Pressable>
      <Pressable
        accessibilityRole="button"
        accessibilityState={{ selected: unitsSelected }}
        onPress={() => onSelect('units')}
        style={[
          styles.compactTab,
          isPhone && styles.compactTabPhone,
          {
            backgroundColor: unitsSelected
              ? colors.buttonDefault
              : colors.backgroundLevel1,
          },
        ]}
      >
        <Typography
          variant={tabVariant}
          color={unitsSelected ? 'white' : 'primary'}
          style={styles.compactTabText}
        >
          {unitsTabLabel}
        </Typography>
      </Pressable>
    </View>
  );
}

/**
 * Desktop rows stay horizontally aligned:
 * 1) greeting
 * 2) status cards (when present)
 * 3) project scroll (hero…) | sticky price + count / units scroll (cards)
 *
 * Compact: both panes stay mounted (hidden with display:none) after warm/first
 * open so tab switches do not remount every unit card.
 */
export function AdhaListingLayoutShell({
  layout,
  compactTab,
  onSelectCompactTab,
  projectTabLabel,
  unitsTabLabel,
  titleRow,
  statusFilterRow,
  sidebarSticky,
  compactHeader,
  compactStatusSticky,
  compactPriceSticky,
  projectColumn,
  unitsColumn,
  unitsScrollPagination,
}: Readonly<AdhaListingLayoutShellProps>) {
  const [unitsPaneReady, setUnitsPaneReady] = useState(!layout.isCompact);

  useEffect(() => {
    if (!layout.isCompact) {
      setUnitsPaneReady(true);
      return;
    }
    if (compactTab === 'units') {
      setUnitsPaneReady(true);
      return;
    }
    const timer = setTimeout(() => {
      setUnitsPaneReady(true);
    }, UNITS_PANE_WARM_MS);
    return () => clearTimeout(timer);
  }, [compactTab, layout.isCompact]);

  if (!layout.isCompact) {
    return (
      <AdhaListingDesktopShell
        titleRow={titleRow}
        statusFilterRow={statusFilterRow}
        sidebarSticky={sidebarSticky}
        projectColumn={projectColumn}
        unitsColumn={unitsColumn}
        unitsScrollPagination={unitsScrollPagination}
      />
    );
  }

  return (
    <AdhaListingCompactShell
      layout={layout}
      compactTab={compactTab}
      onSelectCompactTab={onSelectCompactTab}
      projectTabLabel={projectTabLabel}
      unitsTabLabel={unitsTabLabel}
      compactHeader={compactHeader}
      compactStatusSticky={compactStatusSticky}
      compactPriceSticky={compactPriceSticky}
      projectColumn={projectColumn}
      unitsColumn={unitsColumn}
      unitsScrollPagination={unitsScrollPagination}
      unitsPaneReady={unitsPaneReady}
    />
  );
}

function AdhaListingDesktopShell({
  titleRow,
  statusFilterRow,
  sidebarSticky,
  projectColumn,
  unitsColumn,
  unitsScrollPagination,
}: Readonly<{
  titleRow?: React.ReactNode;
  statusFilterRow?: React.ReactNode;
  sidebarSticky?: React.ReactNode;
  projectColumn: React.ReactNode;
  unitsColumn: React.ReactNode;
  unitsScrollPagination: ReturnType<typeof useScrollPagination>;
}>) {
  const { breakpointWidth } = useWebViewport();
  const isCompactShell = breakpointWidth < BREAKPOINTS.SHELL;
  const pagePadX = getWebPageContentPadding(isCompactShell);

  return (
    <View style={styles.desktopRoot}>
      {titleRow ? (
        <View style={styles.desktopPinnedRow}>{titleRow}</View>
      ) : null}
      {statusFilterRow ? (
        <View style={styles.desktopPinnedRow}>{statusFilterRow}</View>
      ) : null}
      <View style={[styles.splitShell, { paddingHorizontal: pagePadX }]}>
        <ScrollView
          style={styles.mainScroll}
          contentContainerStyle={styles.mainScrollContent}
          showsVerticalScrollIndicator={false}
        >
          {projectColumn}
        </ScrollView>
        <View style={styles.sidebarPane}>
          {sidebarSticky ? (
            <View style={styles.sidebarStickyBlock}>{sidebarSticky}</View>
          ) : null}
          <OverlayScrollView
            alwaysVisible
            style={styles.unitsScrollFrame}
            scrollStyle={styles.sidebarScroll}
            contentContainerStyle={styles.sidebarScrollContent}
            {...unitsScrollPagination}
          >
            {unitsColumn}
          </OverlayScrollView>
        </View>
      </View>
    </View>
  );
}

function AdhaListingCompactShell({
  layout,
  compactTab,
  onSelectCompactTab,
  projectTabLabel,
  unitsTabLabel,
  compactHeader,
  compactStatusSticky,
  compactPriceSticky,
  projectColumn,
  unitsColumn,
  unitsScrollPagination,
  unitsPaneReady,
}: Readonly<{
  layout: AdhaListingLayout;
  compactTab: AdhaListingTab;
  onSelectCompactTab: (tab: AdhaListingTab) => void;
  projectTabLabel: string;
  unitsTabLabel: string;
  compactHeader?: React.ReactNode;
  compactStatusSticky?: React.ReactNode;
  compactPriceSticky?: React.ReactNode;
  projectColumn: React.ReactNode;
  unitsColumn: React.ReactNode;
  unitsScrollPagination: ReturnType<typeof useScrollPagination>;
  unitsPaneReady: boolean;
}>) {
  const showProject = compactTab === 'project';
  const showUnits = compactTab === 'units';

  return (
    <View style={styles.compactShell}>
      {compactHeader}
      <CompactTabs
        compactTab={compactTab}
        isPhone={layout.isPhone}
        projectTabLabel={projectTabLabel}
        unitsTabLabel={unitsTabLabel}
        onSelect={onSelectCompactTab}
      />
      {showProject && compactStatusSticky ? (
        <View style={styles.compactStickyBlock}>{compactStatusSticky}</View>
      ) : null}
      {showUnits && compactPriceSticky ? (
        <View style={styles.compactStickyBlock}>{compactPriceSticky}</View>
      ) : null}
      <View
        style={[
          styles.compactPane,
          !showProject && styles.compactPaneHidden,
        ]}
        accessibilityElementsHidden={!showProject}
        importantForAccessibility={showProject ? 'yes' : 'no-hide-descendants'}
      >
        <ScrollView
          style={styles.compactScroll}
          contentContainerStyle={styles.compactScrollContent}
          showsVerticalScrollIndicator={false}
        >
          {projectColumn}
        </ScrollView>
      </View>
      {unitsPaneReady ? (
        <View
          style={[
            styles.compactPane,
            !showUnits && styles.compactPaneHidden,
          ]}
          accessibilityElementsHidden={!showUnits}
          importantForAccessibility={
            showUnits ? 'yes' : 'no-hide-descendants'
          }
        >
          <OverlayScrollView
            alwaysVisible
            nestedScrollEnabled={false}
            style={styles.unitsScrollFrame}
            scrollStyle={styles.compactScroll}
            contentContainerStyle={styles.compactScrollContent}
            {...unitsScrollPagination}
          >
            {unitsColumn}
          </OverlayScrollView>
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  desktopRoot: {
    flex: 1,
    minHeight: 0,
    gap: 10,
    paddingTop: 4,
  },
  /**
   * Greeting + reservation/appointment row must never flex-shrink.
   * Otherwise the split (hero scroll) steals height and the image overlaps the card.
   */
  desktopPinnedRow: {
    flexGrow: 0,
    flexShrink: 0,
    width: '100%',
    zIndex: 2,
    position: 'relative',
  },
  splitShell: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'stretch',
    minHeight: 0,
    gap: 24,
    overflow: 'hidden',
    zIndex: 0,
  },
  mainScroll: {
    flexGrow: ADHA_LISTING_MAIN_FLEX,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 0,
    minHeight: 0,
    ...(Platform.OS === 'web' ? ({ overflow: 'scroll' } as object) : null),
  },
  mainScrollContent: {
    flexGrow: 0,
    paddingBottom: 32,
    gap: 16,
  },
  /** Right pane: keep MAIN:SIDEBAR flex ratio (no maxWidth — that skewed wide screens). */
  sidebarPane: {
    flexGrow: ADHA_LISTING_SIDEBAR_FLEX,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 0,
    minHeight: 0,
    overflow: 'hidden',
    gap: 16,
  },
  /** Price filter + Associated Units — does not scroll with unit cards. */
  sidebarStickyBlock: {
    flexGrow: 0,
    flexShrink: 0,
    width: '100%',
    zIndex: 2,
  },
  unitsScrollFrame: {
    flex: 1,
    width: '100%',
    minHeight: 0,
  },
  sidebarScroll: {
    flex: 1,
    width: '100%',
    minHeight: 0,
    ...(Platform.OS === 'web' ? ({ overflow: 'scroll' } as object) : null),
  },
  sidebarScrollContent: {
    flexGrow: 0,
    justifyContent: 'flex-start',
    paddingBottom: 32,
    gap: 16,
  },
  compactShell: {
    flex: 1,
    minHeight: 0,
    paddingHorizontal: 0,
    paddingTop: 0,
    gap: 16,
  },
  compactTabs: {
    flexDirection: 'row',
    gap: 8,
    width: '100%',
    flexGrow: 0,
    flexShrink: 0,
  },
  /** Status cards / price filter stay under tabs while pane content scrolls. */
  compactStickyBlock: {
    flexGrow: 0,
    flexShrink: 0,
    width: '100%',
    zIndex: 2,
  },
  compactTab: {
    flex: 1,
    minHeight: 44,
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  /** Narrower label padding on phone so both titles fit on SE-width. */
  compactTabPhone: {
    paddingHorizontal: 8,
    paddingVertical: 8,
    minHeight: 40,
  },
  compactTabText: {
    letterSpacing: 0,
    textAlign: 'center',
  },
  compactPane: {
    flex: 1,
    minHeight: 0,
  },
  compactPaneHidden: {
    display: 'none',
  },
  compactScroll: {
    flex: 1,
    minHeight: 0,
    ...(Platform.OS === 'web' ? ({ overflow: 'scroll' } as object) : null),
  },
  compactScrollContent: {
    flexGrow: 0,
    width: '100%',
    alignSelf: 'stretch',
    paddingBottom: 32,
    gap: 16,
  },
});

export default AdhaListingLayoutShell;
